import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "./components/ui/sonner";

// Components
import { Navbar } from "./components/Navbar";
import { Hero } from "./components/Hero";
import { Services } from "./components/Services";
import { WhyChooseUs } from "./components/WhyChooseUs";
import { Portfolio } from "./components/Portfolio";
import { Process } from "./components/Process";
import { Testimonials } from "./components/Testimonials";
import { ServiceArea } from "./components/ServiceArea";
import { Certifications } from "./components/Certifications";
import { Contact } from "./components/Contact";
import { Footer } from "./components/Footer";
import { CTABanner } from "./components/CTABanner";
import { QuoteForm } from "./components/QuoteForm";
import { AboutPage } from "./components/AboutPage";
import { ServiceAreaPage } from "./components/ServiceAreaPage";
import { WhatsAppButton } from "./components/WhatsAppButton";
import { PrivacyPolicy } from "./components/PrivacyPolicy";
import { TermsOfService } from "./components/TermsOfService";
import { CookieConsent } from "./components/CookieConsent";

// Admin Components
import { AdminLogin } from "./components/admin/AdminLogin";
import { AdminLayout } from "./components/admin/AdminLayout";
import { AdminDashboard } from "./components/admin/AdminDashboard";
import { AdminQuotes } from "./components/admin/AdminQuotes";
import { AdminContacts } from "./components/admin/AdminContacts";
import { AdminNewsletter } from "./components/admin/AdminNewsletter";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Home Page - Following the exact flow
const HomePage = () => {
  return (
    <main>
      {/* Hero: Project image + "Get Free Quote" CTA */}
      <Hero />
      
      {/* Services Overview (Residential, Commercial, Renovation) */}
      <Services showAll={false} />
      
      {/* Why Choose Us (years experience, projects completed, licensed) */}
      <WhyChooseUs />
      
      {/* Our Process (all 6 steps) */}
      <Process showAll={true} />
      
      {/* Featured Projects gallery - 4 total on homepage */}
      <Portfolio showAll={false} limit={4} />
      
      {/* Client Testimonials */}
      <Testimonials />
      
      {/* Service Areas */}
      <section id="service-areas">
        <ServiceArea />
      </section>
      
      {/* CTA: Request Free Estimate */}
      <CTABanner />
    </main>
  );
};

// Services Page - with sub-service descriptions
const ServicesPage = () => {
  return (
    <main className="pt-20">
      <div className="bg-brand-navy py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <span className="inline-block text-xs sm:text-sm font-medium uppercase tracking-widest text-brand-orange mb-2">
            What We Offer
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl font-bold tracking-tight uppercase text-white">
            Our Services
          </h1>
          <p className="font-body text-base sm:text-lg text-white/70 mt-3 max-w-2xl">
            Professional painting services for Orlando and Central Florida homes
          </p>
        </div>
      </div>
      <Services showAll={true} />
      <WhyChooseUs />
      <CTABanner />
    </main>
  );
};

// Portfolio Page - with filters and before/after
const PortfolioPage = () => {
  return (
    <main className="pt-20">
      <div className="bg-brand-navy py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <span className="inline-block text-xs sm:text-sm font-medium uppercase tracking-widest text-brand-orange mb-2">
            Our Work
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl font-bold tracking-tight uppercase text-white">
            Project Portfolio
          </h1>
          <p className="font-body text-base sm:text-lg text-white/70 mt-3 max-w-2xl">
            Browse our completed painting projects with before/after photos from around Central Florida
          </p>
        </div>
      </div>
      <Portfolio showAll={true} />
      <Testimonials />
      <CTABanner />
    </main>
  );
};

// Process Page - Visual timeline
const ProcessPage = () => {
  return (
    <main className="pt-20">
      <div className="bg-brand-navy py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <span className="inline-block text-xs sm:text-sm font-medium uppercase tracking-widest text-brand-orange mb-2">
            How We Work
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl font-bold tracking-tight uppercase text-white">
            Our Process
          </h1>
          <p className="font-body text-base sm:text-lg text-white/70 mt-3 max-w-2xl">
            From free estimate to final walkthrough - a simple, transparent approach to quality painting
          </p>
        </div>
      </div>
      <Process showAll={true} />
      <WhyChooseUs />
      <CTABanner />
    </main>
  );
};

// Contact Page
const ContactPage = () => {
  return (
    <main className="pt-20">
      <div className="bg-brand-navy py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <span className="inline-block text-xs sm:text-sm font-medium uppercase tracking-widest text-brand-orange mb-2">
            Get In Touch
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl font-bold tracking-tight uppercase text-white">
            Contact Us
          </h1>
          <p className="font-body text-base sm:text-lg text-white/70 mt-3 max-w-2xl">
            Ready to transform your home? Get in touch for a free estimate.
          </p>
        </div>
      </div>
      <Contact showForm={true} />
    </main>
  );
};

// Quote Page - Multi-step form
const QuotePage = () => {
  return (
    <main>
      <QuoteForm />
    </main>
  );
};

function App() {
  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Admin Routes - No Navbar/Footer */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="quotes" element={<AdminQuotes />} />
            <Route path="contacts" element={<AdminContacts />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
          </Route>

          {/* Public Routes - With Navbar/Footer */}
          <Route
            path="*"
            element={
              <>
                <Navbar />
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/services" element={<ServicesPage />} />
                  <Route path="/portfolio" element={<PortfolioPage />} />
                  <Route path="/process" element={<ProcessPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/service-areas" element={<ServiceAreaPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/quote" element={<QuotePage />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                </Routes>
                <Footer />
                <WhatsAppButton />
            <CookieConsent />
              </>
            }
          />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
